import { type FC, useMemo, useState } from "react";
import { MetaAdsAudienceStatus } from "shared";
import { LinkGen } from "../../LinkGen";
import { useCampaignWizard } from "../CampaignWizardContext";
import { useAudiencesQuery } from "../useAudiences";

export const AudienceTargetingSection: FC = () => {
	const { draft, updateDraft } = useCampaignWizard();
	const { data: audiences = [], isLoading, isError } = useAudiencesQuery();
	const [includeSearch, setIncludeSearch] = useState("");
	const [excludeSearch, setExcludeSearch] = useState("");

	const included = draft.includedAudiences ?? [];
	const excluded = draft.excludedAudiences ?? [];

	const filteredForInclude = useMemo(() => {
		const q = includeSearch.toLowerCase();
		return audiences
			.filter((a) => !included.includes(a.id) && !excluded.includes(a.id))
			.filter((a) => !q || a.name.toLowerCase().includes(q));
	}, [audiences, included, excluded, includeSearch]);

	const filteredForExclude = useMemo(() => {
		const q = excludeSearch.toLowerCase();
		return audiences
			.filter((a) => !excluded.includes(a.id) && !included.includes(a.id))
			.filter((a) => !q || a.name.toLowerCase().includes(q));
	}, [audiences, excluded, included, excludeSearch]);

	const getAudienceName = (id: string) =>
		audiences.find((a) => a.id === id)?.name ?? id;
	const getAudienceStatus = (id: string) =>
		audiences.find((a) => a.id === id)?.status;

	const warningAudiences = [...included, ...excluded].filter((id) => {
		const status = getAudienceStatus(id);
		return (
			status === MetaAdsAudienceStatus.POPULATING ||
			status === MetaAdsAudienceStatus.TOO_SMALL
		);
	});

	return (
		<div>
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
				}}
			>
				<h6>Audience Targeting</h6>
				<a
					href={LinkGen.MetaAds.Audiences()}
					target="_blank"
					rel="noreferrer"
					style={{ fontSize: 13 }}
				>
					Manage Audiences
				</a>
			</div>

			{isLoading ? (
				<p className="text-muted" style={{ fontSize: 14 }}>
					Loading audiences...
				</p>
			) : isError ? (
				<p className="text-muted" style={{ fontSize: 14 }}>
					Unable to load audiences right now. You can continue without audience
					targeting.
				</p>
			) : audiences.length === 0 ? (
				<p className="text-muted" style={{ fontSize: 14 }}>
					No audiences created yet.{" "}
					<a
						href={LinkGen.MetaAds.Audiences()}
						target="_blank"
						rel="noreferrer"
					>
						Create audiences
					</a>{" "}
					to target or exclude specific groups.
				</p>
			) : (
				<>
					<div style={{ marginBottom: 12 }}>
						<label
							className="form-label"
							style={{ fontSize: 14, fontWeight: 500 }}
							htmlFor="include-audience-search"
						>
							Include Audiences
						</label>
						<input
							id="include-audience-search"
							type="text"
							className="form-control form-control-sm"
							placeholder="Search audiences to include..."
							value={includeSearch}
							onChange={(e) => setIncludeSearch(e.target.value)}
						/>
						{includeSearch && filteredForInclude.length > 0 && (
							<div
								className="list-group"
								style={{
									maxHeight: 150,
									overflowY: "auto",
									position: "absolute",
									zIndex: 10,
									width: "calc(100% - 32px)",
								}}
							>
								{filteredForInclude.slice(0, 8).map((a) => (
									<button
										key={a.id}
										type="button"
										className="list-group-item list-group-item-action py-1"
										onClick={() => {
											updateDraft({
												includedAudiences: [...included, a.id],
											});
											setIncludeSearch("");
										}}
									>
										{a.name}
										<span
											className={`badge ms-2 ${a.status === MetaAdsAudienceStatus.READY ? "bg-success" : "bg-warning"}`}
											style={{ fontSize: 10 }}
										>
											{a.status}
										</span>
									</button>
								))}
							</div>
						)}
						{included.length > 0 && (
							<div
								style={{
									display: "flex",
									flexWrap: "wrap",
									gap: 4,
									marginTop: 8,
								}}
							>
								{included.map((id) => (
									<span
										key={id}
										className="badge bg-primary"
										style={{
											display: "inline-flex",
											alignItems: "center",
											gap: 4,
										}}
									>
										{getAudienceName(id)}
										<button
											type="button"
											className="btn-close btn-close-white"
											style={{ fontSize: 8 }}
											aria-label="Remove"
											onClick={() =>
												updateDraft({
													includedAudiences: included.filter((i) => i !== id),
												})
											}
										/>
									</span>
								))}
							</div>
						)}
					</div>

					<div style={{ marginBottom: 8 }}>
						<label
							className="form-label"
							style={{ fontSize: 14, fontWeight: 500 }}
							htmlFor="exclude-audience-search"
						>
							Exclude Audiences
						</label>
						<input
							id="exclude-audience-search"
							type="text"
							className="form-control form-control-sm"
							placeholder="Search audiences to exclude..."
							value={excludeSearch}
							onChange={(e) => setExcludeSearch(e.target.value)}
						/>
						{excludeSearch && filteredForExclude.length > 0 && (
							<div
								className="list-group"
								style={{
									maxHeight: 150,
									overflowY: "auto",
									position: "absolute",
									zIndex: 10,
									width: "calc(100% - 32px)",
								}}
							>
								{filteredForExclude.slice(0, 8).map((a) => (
									<button
										key={a.id}
										type="button"
										className="list-group-item list-group-item-action py-1"
										onClick={() => {
											updateDraft({
												excludedAudiences: [...excluded, a.id],
											});
											setExcludeSearch("");
										}}
									>
										{a.name}
										<span
											className={`badge ms-2 ${a.status === MetaAdsAudienceStatus.READY ? "bg-success" : "bg-warning"}`}
											style={{ fontSize: 10 }}
										>
											{a.status}
										</span>
									</button>
								))}
							</div>
						)}
						{excluded.length > 0 && (
							<div
								style={{
									display: "flex",
									flexWrap: "wrap",
									gap: 4,
									marginTop: 8,
								}}
							>
								{excluded.map((id) => (
									<span
										key={id}
										className="badge bg-danger"
										style={{
											display: "inline-flex",
											alignItems: "center",
											gap: 4,
										}}
									>
										{getAudienceName(id)}
										<button
											type="button"
											className="btn-close btn-close-white"
											style={{ fontSize: 8 }}
											aria-label="Remove"
											onClick={() =>
												updateDraft({
													excludedAudiences: excluded.filter((i) => i !== id),
												})
											}
										/>
									</span>
								))}
							</div>
						)}
					</div>

					{warningAudiences.length > 0 && (
						<div className="alert alert-warning py-2" style={{ fontSize: 13 }}>
							Some selected audiences are still populating or too small. They
							may not work effectively for targeting.
						</div>
					)}
				</>
			)}
		</div>
	);
};
